import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View, Text } from './components/base_components';

import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';
import BoxPropList from './components/box_prop_list';
import MediaGrantSelect from './components/media_grant_select';

import DeviceStore from './stores/device_store';
import { useOrg } from './stores/user_store';

import { dateFormat, relativeDate } from './tools/util';

const styles = StyleSheet.create({
  deviceScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  empty: {
    margin: '2em',
    fontSize: '1.5em',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  input: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
});

export default function DeviceScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = useOrg();
  const device_id = parseInt(params.device_id ?? '0', 10) || 0;
  const device = DeviceStore.useDevice(device_id);
  const [add_grant, setAddGrant] = React.useState(0);
  const [selected_grants, setSelectedGrants] = React.useState<any[]>([]);
  const [updates, setUpdates] = React.useState<any>({});
  const org_id = org?.org_id;

  const selected_grant = device?.media_grant_list?.[selected_grants?.[0]];
  const selected_grant_id = selected_grant?.media_grant_id;

  React.useEffect(() => {
    DeviceStore.fetchDevice(device_id);
  }, [device_id]);

  function _onAddGrantPress() {
    if (add_grant && setBusy()) {
      const opts = { org_id, device_id, media_grant_id: add_grant };
      DeviceStore.addMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add device mount, please try again.');
        }
      });
    }
  }
  function _onEnableGrantPress() {
    if (selected_grant_id && setBusy()) {
      const opts = { org_id, device_id, media_grant_id: selected_grant_id };
      DeviceStore.enableMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to enable device mount, please try again.');
        }
      });
    }
  }
  function _onDisableGrantPress() {
    if (selected_grant_id && setBusy()) {
      const opts = { org_id, device_id, media_grant_id: selected_grant_id };
      DeviceStore.disableMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to disable device mount, please try again.');
        }
      });
    }
  }
  function _onDeviceGrantKeyPress() {
    const yes = window.confirm(
      'This potentially exposes the entire contents of this volume if the key is leaked. Are you sure you want to get the key for this volume?'
    );
    if (yes) {
      const opts = { org_id, device_id, media_grant_id: selected_grant_id };
      DeviceStore.getDeviceMediaGrantKey(opts, (err, key) => {
        if (err) {
          window.alert('Failed to get volume key.');
        } else {
          window.alert('Volume Key: ' + key);
        }
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = {
        org_id: org?.org_id,
        device_id,
      };
      if (updates.device_name) {
        opts.device_name = updates.device_name;
      }
      if (updates.is_disabled !== undefined) {
        opts.is_disabled = updates.is_disabled === 'Disabled';
      }
      if (updates.is_upload_disabled !== undefined) {
        opts.is_upload_disabled = updates.is_upload_disabled === 'Disabled';
      }
      if (updates.is_download_disabled !== undefined) {
        opts.is_download_disabled = updates.is_download_disabled === 'Disabled';
      }
      if (updates.upload_priority !== undefined) {
        opts.upload_priority = updates.upload_priority;
      }
      if (updates.user_id !== undefined) {
        opts.user_id = updates.user_id;
      }
      DeviceStore.updateDevice(opts, (err) => {
        clearBusy();
        if (err === 'max_device_count') {
          window.alert('Sorry, your plan limit for devices has been reached.');
        } else if (err === 402) {
          window.alert('Sorry, you plan does not allow this change.');
        } else if (err) {
          window.alert('Failed to update device.');
        } else {
          complete?.();
        }
      });
    }
  }

  let grants: any = null;
  if (!device) {
    grants = <ProgressSpinner />;
  } else {
    if (!device?.media_grant_list?.length) {
      grants = <Text style={styles.empty}>No Device Mounts.</Text>;
    }
  }

  return (
    <View style={styles.deviceScreen}>
      <Breadcrumb
        list={[{ text: 'Devices', url: '/device' }, device?.device_name]}
      />
      <TitleActionLine title={device?.device_name} />
      <BoxPropList
        title='Device Details'
        editable={org?.is_admin}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={[
          [
            {
              key: 'device_name',
              label: 'Device Name',
              value: device?.device_name,
              input: {
                placeholder: 'Device Name',
              },
            },
            {
              key: 'user_id',
              label: 'User',
              value: device?.user_name,
              initialValue: device?.user_id,
              userSelect: {
                orgId: org.org_id,
              },
            },
            {
              key: 'is_disabled',
              label: 'Status',
              value: device?.is_disabled ? 'Disabled' : 'Enabled',
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              label: 'Last Seen',
              value: dateFormat(device?.last_seen) || '-',
            },
          ],
          [
            {
              key: 'is_download_disabled',
              label: 'Download Status',
              value: device?.is_download_disabled ? 'Disabled' : 'Enabled',
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              key: 'is_upload_disabled',
              label: 'Upload Status',
              value: device?.is_upload_disabled ? 'Disabled' : 'Enabled',
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              key: 'upload_priority',
              label: 'Upload Priority',
              value: String(device?.upload_priority ?? 0),
              input: {
                type: 'number',
                min: 0,
                max: 1000,
              },
            },
            {
              label: 'Last Version',
              value: device?.last_version || '-',
            },
          ],
        ]}
      />
      <StandardBoxList
        title='Device Mounts'
        detail={device?.media_grant_list?.length}
        headers={[
          'Container Name',
          'Volume Name',
          'Prefix Path',
          'Status',
          'Last Seen',
        ]}
        sizes={[2, 2, 2]}
        buttons={
          <>
            {org?.is_admin ? (
              <TextButton
                style={styles.actionButton}
                text='Get Volume Key'
                disabled={!selected_grant_id}
                onPress={_onDeviceGrantKeyPress}
              />
            ) : null}
            <TextButton
              style={styles.actionButton}
              text='Enable'
              disabled={selected_grant?.is_disabled !== 1}
              onPress={_onEnableGrantPress}
            />
            <TextButton
              style={styles.actionButton}
              text='Disable'
              disabled={selected_grant?.is_disabled !== 0}
              onPress={_onDisableGrantPress}
            />
          </>
        }
        singleSelect={true}
        selected={selected_grants}
        onChange={setSelectedGrants}
        rows={device?.media_grant_list?.map?.((grant: any) => ({
          list: [
            grant.grant_name,
            grant.volume_name,
            grant.prefix_path,
            grant.is_disabled ? 'Disabled' : 'Enabled',
            relativeDate(grant.last_seen) || '-',
          ],
        }))}
      >
        {grants}
      </StandardBoxList>
      <StandardBoxList title='Add Device Mount'>
        <View style={styles.dialog}>
          <Text style={styles.text}>Add a device mount.</Text>
          <MediaGrantSelect
            style={styles.input}
            placeholder='Pick Volume'
            orgId={org_id}
            isAdmin={org?.is_admin}
            excludeList={device?.media_grant_list}
            value={add_grant}
            onChange={setAddGrant}
          />
          <TextButton
            style={styles.button}
            text='Add Grant'
            onPress={_onAddGrantPress}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
